/**
 * Why a dictation session failed to start, in words the person at the keyboard
 * can act on.
 *
 * This exists because every failure used to collapse into one sentence:
 * "Could not start recording. Check microphone permissions." That sentence named
 * the one cause it could never have — `TranscribeSession.start()` hands every
 * `getUserMedia` rejection to `onError` and returns (transcribeStream.ts), so a
 * blocked microphone never reached the toast. What did reach it were the two
 * failures that have nothing to do with the microphone: our own credentials call
 * rejecting, and the browser refusing to build the audio graph.
 *
 * A clinic reported exactly that and nobody could tell which had happened,
 * because the message was the same either way and the server logged nothing on
 * success. Each cause now carries a short reference code so a support request
 * names its own branch without anyone reading a log.
 */

import { ApiRequestError } from './api';
import { describeMediaError } from './voiceErrors';

/** Short, support-searchable tag appended to the message the user sees. */
export type DictationErrorRef =
  | 'DICT-ACCESS'
  | 'DICT-BUSY'
  | 'DICT-OFFLINE'
  | 'DICT-AUDIO'
  | 'DICT-MIC'
  | 'DICT-STREAM';

export interface DictationErrorDescription {
  /** The full sentence to show, reference code included. */
  message: string;
  ref: DictationErrorRef;
}

/**
 * The browser refused to build the audio graph after the microphone opened.
 *
 * Thrown by `TranscribeSession.start()` so this case stays distinguishable from
 * a microphone fault; `cause` keeps the underlying DOMException for the console.
 */
export class DictationAudioSetupError extends Error {
  readonly cause: unknown;
  /**
   * Which worklet URL had loaded when setup failed: `asset` same-origin, `blob`
   * if the fallback was reached, `null` if neither registered. This is the
   * signal that tells us whether blob-URL restrictions are involved.
   */
  readonly workletLoadedFrom: 'asset' | 'blob' | null;

  constructor(cause: unknown, workletLoadedFrom: 'asset' | 'blob' | null = null) {
    super('Audio capture could not be initialised in this browser.');
    this.name = 'DictationAudioSetupError';
    this.cause = cause;
    this.workletLoadedFrom = workletLoadedFrom;
  }
}

const CAUSES: Record<DictationErrorRef, string> = {
  'DICT-ACCESS':
    "Dictation isn't available right now. Ask your system administrator to check the dictation setup.",
  'DICT-BUSY': 'Too many recording attempts. Wait a minute and try again.',
  'DICT-OFFLINE':
    "Couldn't reach Prospera to start dictation. Check your internet connection and try again.",
  'DICT-AUDIO':
    'Your browser blocked audio recording. Try a different browser, or ask IT whether a browser extension or security policy is blocking it.',
  'DICT-MIC': '',
  'DICT-STREAM':
    'Dictation stopped unexpectedly. Try recording again — anything already transcribed has been kept.',
};

/** Device-fault names browsers report through `DOMException.name`. */
const MEDIA_ERROR_NAMES = new Set([
  'AbortError',
  'NotAllowedError',
  'NotFoundError',
  'NotReadableError',
  'OverconstrainedError',
  'SecurityError',
]);

function describe(ref: DictationErrorRef, sentence?: string): DictationErrorDescription {
  const body = sentence ?? CAUSES[ref];
  return { message: `${body} (Ref: ${ref})`, ref };
}

function isMediaError(err: unknown): boolean {
  if (typeof DOMException !== 'undefined' && err instanceof DOMException) return true;
  if (!err || typeof err !== 'object') return false;
  const name = (err as { name?: unknown }).name;
  return typeof name === 'string' && MEDIA_ERROR_NAMES.has(name);
}

/**
 * Classify anything thrown while starting or running dictation.
 *
 * Order matters: the typed cases are checked before the shape-based ones, so a
 * server response is never mistaken for a device fault.
 */
export function describeDictationError(err: unknown): DictationErrorDescription {
  if (err instanceof DictationAudioSetupError) return describe('DICT-AUDIO');

  if (err instanceof ApiRequestError) {
    if (err.status === 429 || err.code === 'RATE_LIMITED') return describe('DICT-BUSY');
    return describe('DICT-ACCESS');
  }

  // The microphone prompt going unanswered has its own sentence already, and it
  // is the one message here that is not about a fault at all.
  if (err instanceof Error && err.message.includes('timed out')) {
    return describe('DICT-MIC', err.message);
  }

  if (isMediaError(err)) return describe('DICT-MIC', describeMediaError(err));

  // `fetch` rejects with a TypeError when the request never reached a server —
  // offline, DNS failure, or a connection cut mid-flight.
  if (err instanceof TypeError) return describe('DICT-OFFLINE');

  return describe('DICT-STREAM');
}
